import React, { Fragment, useState, useContext } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Collapse } from 'react-collapse';
import { ICONS } from 'Components/utils/icon/icons';
import { ActivitiesContext } from 'Context/ActivitiesContext';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ServiceLine } from './ServiceLine';
type Props = {
  attendeeId: string;
  className?: string;
  currency: string;
  firstName?: string;
  index: number;
  isAdult: boolean;
  lastName?: string;
  noEditMode: boolean;
  rentedServices: ReservedServicesByAttendee['rentedServices'];
  unicodeFirstName?: string;
  unicodeLastName?: string;
};
export const ReservedServiceBlock = ({
  attendeeId,
  className,
  isAdult,
  firstName,
  lastName,
  unicodeLastName,
  unicodeFirstName,
  index,
  rentedServices,
  noEditMode,
  currency,
}: Props) => {
  const { locale } = useAppContext();
  const [opened, setOpened] = useState(false);
  const { deleteService } = useContext(ActivitiesContext);
  const withLoadMore = rentedServices.length > 2 && !opened;
  const total = rentedServices.reduce((totalPrice, { price }) => totalPrice + price, 0);

  const onDelete = (services) => deleteService(services, attendeeId);

  const attendeeFirstName = unicodeFirstName?.trim() || firstName?.trim() || '';
  const attendeeLastName = unicodeLastName?.trim() || lastName?.trim() || '';
  const attendeelabel = `${String(attendeeFirstName)}${attendeeFirstName ? ' ' : ''}${String(attendeeLastName)}`;
  return (
    <div
      className={classnames('max-w-280 text-start', className)}
      data-testid="ReservedServiceBlock"
    >
      <Capsule header="small">
        <div className="relative flex min-h-175 flex-col font-sans text-14">
          <header
            className="label air360-replay-ignore px-10 pt-10 text-20 font-bold capitalize text-black-active"
            data-cs-mask
          >
            {attendeelabel ||
              (isAdult
                ? `${t('booking.common.adult')} ${index}`
                : `${t('booking.common.child')} ${index}`)}
          </header>
          <section
            className="relative z-0 min-h-88 px-10 text-14"
            data-testid="ReservedServiceBlock--details"
          >
            {withLoadMore && (
              <div className="absolute z-1 h-full w-full bg-[linear-gradient(180deg,rgba(0,0,0,0),rgba(255,255,255,1)_80%)]" />
            )}

            <ul className="space-y-10">
              {rentedServices.slice(0, 2).map(({ price, services }) => (
                <ServiceLine
                  currency={currency}
                  key={services[0].pricingInfo.id}
                  label={services[0].productInfo.label}
                  noEditMode={noEditMode}
                  onDelete={onDelete}
                  price={price}
                  services={services}
                />
              ))}

              {rentedServices.length > 2 && (
                <Collapse isOpened={opened}>
                  {rentedServices.slice(2).map(({ price, services }) => (
                    <ServiceLine
                      currency={currency}
                      key={services[0].pricingInfo.id}
                      label={services[0].productInfo.label}
                      noEditMode={noEditMode}
                      onDelete={onDelete}
                      price={price}
                      services={services}
                    />
                  ))}
                </Collapse>
              )}
            </ul>
          </section>

          <footer className="flex w-full flex-row items-end justify-between self-end border-t-1 border-grey p-10">
            {rentedServices.length > 2 && (
              <button
                className="inline-flex items-center gap-2 text-12 leading-tight text-black underline hocus:text-black-active"
                onClick={() => setOpened(!opened)}
                type="button"
              >
                {!opened ? (
                  <Fragment>
                    {t('common.loadMore')}
                    &nbsp;
                    <Icon name={ICONS.PLUS} width="1em" />
                  </Fragment>
                ) : (
                  <Fragment>
                    {t('common.loadLess')}
                    &nbsp;
                    <Icon name={ICONS.MINUS} width="1em" />
                  </Fragment>
                )}
              </button>
            )}
            <span
              className="flex-1 text-right text-20 font-bold text-sienna"
              data-testid="ReservedServiceBlock--amount"
            >
              {formatAmountForLocale(total, locale, currency)}
            </span>
          </footer>
        </div>
      </Capsule>
    </div>
  );
};
